import { COMMENT_MAX, WAITING_MESSAGE_REQUIRED } from '@hrms/shared'
import { useId, useRef, useState } from 'react'
import { LuSend } from 'react-icons/lu'
import { Button, Modal, TextArea } from '../../../components/ui/index.ts'
import { hint } from '../../../lib/hint.ts'

/**
 * "What do you need from Priya?" (DESIGN.md §3.7). Opened before any move into a Waiting status
 * (side panel select now, board drop in Wave 5). `onSubmit` resolves true when saved; cancelling
 * leaves the status as it was.
 */
export function WaitPrompt({
  ticketKey,
  firstName,
  statusName,
  onSubmit,
  onCancel,
}: {
  ticketKey: string
  firstName: string
  statusName: string
  onSubmit: (message: string) => Promise<boolean>
  onCancel: () => void
}) {
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)
  const ids = { text: useId(), err: useId(), copy: useId() }

  const submit = async () => {
    if (busy) return
    const message = text.trim()
    if (!message) {
      setError(WAITING_MESSAGE_REQUIRED)
      ref.current?.focus()
      return
    }
    setBusy(true)
    const ok = await onSubmit(message)
    setBusy(false)
    if (!ok) ref.current?.focus()
  }

  return (
    <Modal
      title={`What do you need from ${firstName}?`}
      onClose={onCancel}
      width={540}
      initialFocus={ref}
      footer={
        <>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={busy}
            {...hint('Ctrl or Cmd + Enter')}
          >
            <LuSend aria-hidden className="size-3.5" />
            {busy ? 'Sending…' : `Send and mark as ${statusName.toLowerCase()}`}
          </Button>
        </>
      }
    >
      <p id={ids.copy} className="mb-3 text-[13.5px] text-muted">
        This is posted as a reply on {ticketKey}. The ETA pauses until {firstName} responds, and the
        ticket comes back to you when they do.
      </p>
      <label htmlFor={ids.text} className="sr-only">
        Message to {firstName}
      </label>
      <TextArea
        ref={ref}
        id={ids.text}
        value={text}
        maxLength={COMMENT_MAX}
        invalid={!!error}
        aria-describedby={error ? `${ids.copy} ${ids.err}` : ids.copy}
        placeholder="For example: could you share the hotel invoice with the GST number on it?"
        className="min-h-[110px]"
        onChange={(e) => {
          setText(e.target.value)
          if (error) setError(null)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            void submit()
          }
        }}
      />
      {error && (
        <p id={ids.err} className="mt-1.5 text-[12.5px] text-danger">
          {error}
        </p>
      )}
    </Modal>
  )
}
